import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Lock,
  Check,
} from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { kycService } from '../../services/kyc';
import LoadingSpinner from '../UI/LoadingSpinner';
import { Button } from '../new-components/ui/button';

interface KYCVerificationProps {
  onVerificationComplete?: (status: 'verified' | 'pending' | 'rejected') => void;
}

interface KYCStatus {
  status: string;
  sessionId?: string;
}

type StatusTone = 'default' | 'success' | 'warning' | 'destructive';

// Icon chip color by tone — neutral for the informational states (no brand/gold fill), and
// semantic tokens only where they carry meaning: `--success` green for verified, `--destructive`
// red for failure. Matches the neutral icon chips used across the redesigned settings screens.
const TONE: Record<StatusTone, { bg: string; fg: string }> = {
  default: { bg: 'bg-secondary', fg: 'text-foreground' },
  success: { bg: 'bg-[hsl(var(--success))]/10', fg: 'text-[hsl(var(--success))]' },
  warning: { bg: 'bg-secondary', fg: 'text-foreground' },
  destructive: { bg: 'bg-destructive/10', fg: 'text-destructive' },
};

const KYCVerification: React.FC<KYCVerificationProps> = ({
  onVerificationComplete
}) => {
  const { refreshUser } = useUser();

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [kycStatus, setKycStatus] = useState<KYCStatus | null>(null);

  // Track the live polling interval and mounted state so we can stop polling (and avoid
  // setState-after-unmount) when the user navigates away mid-verification. This component is
  // mounted live in Settings and Profile, so an uncleaned interval would fire forever.
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    // Reset on every mount, not just at useRef init — otherwise StrictMode's dev double-invoke
    // (mount → cleanup sets false → mount) leaves the flag latched false, and the status poll
    // would kill itself on its first tick.
    mountedRef.current = true;
    fetchKYCStatus();
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const fetchKYCStatus = async () => {
    try {
      setIsFetching(true);
      const response = await kycService.getKycStatus();
      const info = response.data || response;

      if (response.success) {
        setKycStatus({
          status: (info as any).status || 'none',
          sessionId: (info as any).sessionId,
        });
      }
    } catch (error) {
      console.error('Failed to fetch KYC status:', error);
      // Set default status if fetch fails
      setKycStatus({ status: 'none' });
    } finally {
      setIsFetching(false);
    }
  };

  // Open verification in a new window and poll for status
  const openVerificationWindow = (verificationUrl: string) => {
    console.log('🔗 Opening Plaid IDV URL:', verificationUrl);

    const verificationWindow = window.open(
      verificationUrl,
      'plaid_verification',
      'width=600,height=800,left=100,top=100'
    );

    if (verificationWindow) {
      toast.success('Verification window opened. Complete the process.');

      // Poll for status updates
      let pollCount = 0;
      const maxPolls = 120; // 10 minutes with 5s intervals

      const pollInterval = setInterval(async () => {
        if (!mountedRef.current) { clearInterval(pollInterval); return; }
        pollCount++;

        try {
          const statusResponse = await kycService.getKycStatus();
          if (!mountedRef.current) { clearInterval(pollInterval); return; }
          const responseData = (statusResponse as any).data || statusResponse as any;

          if (statusResponse.success && responseData.status) {
            const newStatus = responseData.status;
            console.log(`📊 IDV status (poll ${pollCount}):`, newStatus);

            if (newStatus === 'verified' || newStatus === 'success') {
              clearInterval(pollInterval);
              if (!verificationWindow.closed) verificationWindow.close();
              await fetchKYCStatus();
              await refreshUser();
              onVerificationComplete?.('verified');
              setIsLoading(false);
            } else if (newStatus === 'failed' || newStatus === 'expired') {
              clearInterval(pollInterval);
              toast.error('Verification failed. Please try again.');
              if (!verificationWindow.closed) verificationWindow.close();
              await fetchKYCStatus();
              onVerificationComplete?.('rejected');
              setIsLoading(false);
            }
          }
        } catch (error) {
          console.error('Poll error:', error);
        }

        if (!mountedRef.current) { clearInterval(pollInterval); return; }

        // Check if window was closed
        if (verificationWindow.closed) {
          clearInterval(pollInterval);
          toast('Verification window closed. Click the button to try again.');
          await fetchKYCStatus();
          setIsLoading(false);
        }

        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
          toast('Verification is processing. Check back shortly.');
          await fetchKYCStatus();
          setIsLoading(false);
        }
      }, 5000);
      // Clear any poll still running from a previous start/retry so we never leave two intervals
      // hitting the status endpoint at once.
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = pollInterval;
    } else {
      toast.error('Please allow popups to complete verification');
      setIsLoading(false);
    }
  };

  const startKYCVerification = async () => {
    setIsLoading(true);

    try {
      console.log('🔄 Starting Plaid IDV verification...');
      toast.loading('Creating verification session...');

      // Create Plaid Identity Verification session
      const response = await kycService.createKycSession();

      console.log('📥 Plaid IDV Session Response:', response);
      toast.dismiss();

      const sessionData = (response.data || response) as any;

      if (response.success) {
        if (sessionData.alreadyVerified) {
          await fetchKYCStatus();
          setIsLoading(false);
          return;
        }

        // Get the verification URL
        const verificationUrl = sessionData.url || sessionData.shareable_url;

        if (verificationUrl) {
          console.log('🔗 Got Plaid IDV URL:', verificationUrl);
          openVerificationWindow(verificationUrl);
        } else {
          toast.error('No verification session available');
          console.error('❌ No URL in response:', response);
          setIsLoading(false);
        }
      } else {
        toast.error((response as any).message || 'Failed to create verification session');
        console.error('❌ Failed response:', response);
        setIsLoading(false);
      }

    } catch (error: any) {
      console.error('❌ KYC verification error:', error);
      toast.dismiss();
      toast.error(error.message || 'An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const restartVerification = async () => {
    setIsLoading(true);
    try {
      toast.loading('Restarting verification...');
      const abandonResponse = await kycService.abandonKyc();
      toast.dismiss();

      if (!abandonResponse.success) {
        toast.error((abandonResponse as any).message || 'Failed to restart verification');
        setIsLoading(false);
        return;
      }

      await refreshUser();
      await fetchKYCStatus();
      toast.success('Verification restarted. Starting a new session...');

      // Start a fresh session immediately
      await startKYCVerification();
    } catch (error: any) {
      console.error('Restart verification error:', error);
      toast.dismiss();
      toast.error(error.message || 'Failed to restart verification');
      setIsLoading(false);
    }
  };

  const retryVerification = async () => {
    setIsLoading(true);

    try {
      toast.loading('Creating new verification session...');

      const response = await kycService.retryKyc();
      toast.dismiss();

      const sessionData = (response.data || response) as any;

      if (response.success) {
        const verificationUrl = sessionData.url || sessionData.shareable_url;

        if (verificationUrl) {
          openVerificationWindow(verificationUrl);
        } else {
          toast.error('Failed to create new session');
          setIsLoading(false);
        }
      } else {
        toast.error((response as any).message || 'Failed to create new session');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Retry verification error:', error);
      toast.dismiss();
      toast.error('Failed to create new verification session');
      setIsLoading(false);
    }
  };

  const getStatusInfo = () => {
    const status = kycStatus?.status || 'none';

    switch (status) {
      case 'none':
      case 'not_started':
        return {
          title: 'Verify your identity',
          description: 'To invest, you need to complete identity verification as required by securities regulations.',
          buttonText: 'Start verification',
          Icon: FileText,
          tone: 'default' as StatusTone,
          showButton: true,
          action: startKYCVerification
        };
      case 'created':
      case 'requires_input':
      case 'active':
      case 'in_progress':
        return {
          title: 'Verification in progress',
          description: 'Please complete your identity verification. Continue where you left off.',
          buttonText: 'Continue verification',
          Icon: Clock,
          tone: 'warning' as StatusTone,
          showButton: true,
          action: startKYCVerification
        };
      case 'processing':
      case 'pending':
      case 'pending_review':
        return {
          title: 'Verification being processed',
          description: 'Your identity verification is being reviewed. This typically takes a few minutes — you\'ll be notified when it\'s complete.',
          buttonText: 'Check status',
          Icon: Clock,
          tone: 'warning' as StatusTone,
          showButton: true,
          action: fetchKYCStatus
        };
      case 'verified':
        return {
          title: 'Identity verified',
          description: 'Your identity has been successfully verified. You can now invest and access all platform features.',
          buttonText: '',
          Icon: CheckCircle2,
          tone: 'success' as StatusTone,
          showButton: false,
          action: undefined
        };
      case 'failed':
      case 'expired':
      case 'rejected':
        return {
          title: 'Verification failed',
          description: 'Your identity verification was unsuccessful. Please try again with valid documents.',
          buttonText: 'Try again',
          Icon: XCircle,
          tone: 'destructive' as StatusTone,
          showButton: true,
          action: retryVerification
        };
      default:
        return {
          title: 'Unknown status',
          description: 'Please refresh the page or contact support.',
          buttonText: 'Refresh status',
          Icon: HelpCircle,
          tone: 'default' as StatusTone,
          showButton: true,
          action: fetchKYCStatus
        };
    }
  };

  if (isFetching) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 portal-typography">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-muted-foreground">Loading verification status…</span>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo();
  const tone = TONE[statusInfo.tone];

  // Verified is terminal — show an in-place success screen (no toast, no navigation). The onboarding
  // banner and the Settings "Verified" row reflect the same state elsewhere.
  if (kycStatus?.status === 'verified') {
    return (
      <div className="rounded-xl border border-border bg-card p-6 portal-typography">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--success))]/10">
            <CheckCircle2 className="h-6 w-6 text-[hsl(var(--success))]" aria-hidden="true" />
          </div>
          <h3 className="mb-2 text-xl font-semibold text-foreground">Identity verified</h3>
          <p className="mx-auto max-w-md text-muted-foreground">
            Your identity has been verified. You can now invest and access all platform features.
          </p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-border bg-card p-6 portal-typography"
    >
      <div className="text-center">
        {/* Status icon */}
        <div className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${tone.bg}`}>
          <statusInfo.Icon className={`h-6 w-6 ${tone.fg}`} aria-hidden="true" />
        </div>

        <h3 className="mb-2 text-xl font-semibold text-foreground">
          {statusInfo.title}
        </h3>

        <p className="mx-auto mb-6 max-w-md text-muted-foreground">
          {statusInfo.description}
        </p>

        {/* Actions */}
        <div className="flex items-center justify-center gap-3">
          {statusInfo.showButton && (
            <Button
              onClick={statusInfo.action}
              disabled={isLoading}
              className="gap-2 bg-foreground text-background hover:bg-foreground/90 focus-visible:border-foreground focus-visible:ring-foreground/50"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Processing…
                </>
              ) : (
                statusInfo.buttonText
              )}
            </Button>
          )}

          {kycStatus?.status && !['none', 'not_started', 'verified'].includes(kycStatus.status) && (
            <Button
              variant="outline"
              onClick={restartVerification}
              disabled={isLoading}
              className="text-muted-foreground"
            >
              Reset
            </Button>
          )}
        </div>

        {/* Requirements */}
        {(kycStatus?.status === 'none' || kycStatus?.status === 'not_started') && (
          <div className="mt-8 text-left">
            <h4 className="mb-3 font-medium text-foreground">
              What you'll need:
            </h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {[
                'Government-issued photo ID (driver\'s license, passport)',
                'Live selfie verification',
                '5–10 minutes to complete the process',
              ].map((req, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check className="h-4 w-4 flex-shrink-0 text-[hsl(var(--success))]" aria-hidden="true" />
                  {req}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Security notice */}
        <div className="mt-6 flex items-start gap-2 rounded-lg bg-secondary p-4 text-left">
          <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="text-xs text-muted-foreground">
            Your personal information is encrypted and securely processed by Plaid Identity Verification.
            We comply with all applicable privacy and data protection regulations.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default KYCVerification;
